import Vue from 'vue'
import VueI18n from 'vue-i18n'
import { LocaleNames, LanguageCountryCodes } from '@/enums/general'
import { stripLanguageRegex } from '@/utils/general'
import i18nConfig from '@/config/i18n'
import { isDevelopment } from '@/enums/app'

Vue.use(VueI18n)

async function loadLocaleMessages (i18n, locale, fallbackLocale) {
  if (Object.keys(i18n.messages).includes(locale)) {
    return
  }
  try {
    let messages

    try {
      const timestamp = new Date().getTime()
      const response = process.env.LOCIZE_USE_CDN_URL === '0'
        ? await fetch(`https://api.locize.app/${process.env.LOCIZE_PROJECT_ID}/${isDevelopment ? 'development' : 'production'}/${locale}/translations?t=${timestamp}`, { cache: 'no-cache' })
        : await fetch(`${process.env.CDN_URL}/translations/${locale}.json`, { cache: 'no-cache' })

      messages = await response.json()
    } catch (error) {
    }

    i18n.setLocaleMessage(locale, messages)
  } catch (err) {
    if (locale !== fallbackLocale) {
      return loadLocaleMessages(i18n, fallbackLocale, fallbackLocale)
    }
  }

  return Vue.nextTick()
}

const langParam = ':lang'

const getI18nParam = (locale, country) => `${locale}-${country}`

const setHtmlLangAttribute = (app, to) => {
  const languageParam = to.fullPath.match(stripLanguageRegex)

  app.head.htmlAttrs = {
    lang: languageParam ? `${languageParam[0][1]}${languageParam[0][2]}` : i18nConfig.defaultLocale
  }
}

const resetHrefLangTags = (app) => {
  // href language tags are the only ones with rel="alternate"
  app.head.link = app.head.link.filter(item => item.rel !== 'alternate')
}

const generateHrefLangTags = (pathWithSlash) => {
  return Object.values(LanguageCountryCodes).map((code) => {
    const urlCode = code.replace('_', '-').toLowerCase() // go from nl_NL to nl-nl
    return { rel: 'alternate', hid: `lang_${code}`, hreflang: urlCode, href: `${process.env.BASE_URL}/${urlCode}${pathWithSlash}` }
  })
}

const setCurrentHrefLangTags = (app, to) => {
  const pathWithSlashWithoutLanguageCode = to.fullPath.replace(stripLanguageRegex, '')
  const hrefLangTags = [
    ...generateHrefLangTags(pathWithSlashWithoutLanguageCode),
    {
      rel: 'alternate',
      hid: 'lang_default',
      hreflang: 'x-default',
      href: `${process.env.BASE_URL}/en-nl${pathWithSlashWithoutLanguageCode}`
    }
  ]
  app.head.link.push(...hrefLangTags)
}

export default async ({ app, store, route, redirect }) => {
  const langCode = route.path.match(/\/([a-z]{2})-.*?\//)
  const locale = langCode && langCode.length > 0 ? langCode[1] : store.state.app.locale
  const fallbackLocale = i18nConfig.defaultLocale

  // after every route, remove old hreflang tags, push in the new hreflang tags.
  app.router.afterEach((to) => {
    setHtmlLangAttribute(app, to)
    resetHrefLangTags(app)
    setCurrentHrefLangTags(app, to)
  })

  app.i18n = new VueI18n({
    locale,
    fallbackLocale,
    silentTranslationWarn: true
  })

  await loadLocaleMessages(app.i18n, locale, fallbackLocale)

  app.i18n.switchLocalePath = async (locale, country = null) => {
    const currenti18nParam = `${store.state.app.locale}-${store.state.app.country}`
    const i18nCountry = country || store.state.app.country
    const i18nParam = getI18nParam(locale, i18nCountry)

    const newPath = app.router.currentRoute.fullPath.replace(currenti18nParam, i18nParam)

    await loadLocaleMessages(app.i18n, locale, fallbackLocale)

    return redirect(newPath)
  }

  app.i18n.localePath = ({ name, params = {}, hash }) => {
    const slug = app.router.options.routes.filter(singleRoute => singleRoute.name === `lang-${name}`)

    if (name === '/') {
      slug.push({ path: `${name}${langParam}${name}` })
    }

    if (!slug.length) {
      return '/'
    }

    const i18nParam = getI18nParam(app.i18n.locale, store.state.app.country)
    let newSlug = slug[0].path.replace(langParam, i18nParam).replace(/\?/g, '')

    Object.keys(params).forEach((key) => {
      newSlug = newSlug.replace(`:${key}`, params[key])
    })

    if (hash) {
      newSlug += `#${hash}`
    }

    return newSlug
  }

  app.i18n.getCurrentLocalePath = () => {
    return app.router.history.current.name.replace('lang-', '')
  }

  app.i18n.locales = Object.keys(LocaleNames).map(code => ({
    code,
    name: LocaleNames[code]
  }))
}
